import React, { useState, useEffect } from "react";
import { Download } from "lucide-react";
import { motion } from "framer-motion";

// Global store for the deferred prompt
let globalDeferredPrompt: any = null;
let globalIsInstallable = false;
const listeners = new Set<(isInstallable: boolean) => void>();

// Capture the event as early as possible
if (typeof window !== "undefined") {
    window.addEventListener('beforeinstallprompt', (e) => {
        e.preventDefault();
        globalDeferredPrompt = e;
        globalIsInstallable = true;
        listeners.forEach(l => l(true));
    });

    window.addEventListener('appinstalled', () => {
        globalIsInstallable = false;
        globalDeferredPrompt = null;
        listeners.forEach(l => l(false));
        console.log('PWA was installed');
    });
}

interface PWAInstallButtonProps {
    className?: string;
    showLabel?: boolean;
}

const PWAInstallButton: React.FC<PWAInstallButtonProps> = ({ className = "", showLabel = true }) => {
    const [isVisible, setIsVisible] = useState(globalIsInstallable);
    const [isInstalled, setIsInstalled] = useState(false);

    useEffect(() => {
        // Check if app is already installed
        if (window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone) {
            setIsInstalled(true);
        }

        const updateVisibility = (installable: boolean) => {
            setIsVisible(installable);
        };

        listeners.add(updateVisibility);
        return () => {
            listeners.delete(updateVisibility);
        };
    }, []);

    const handleInstallClick = async () => {
        if (!globalDeferredPrompt) return;

        // Show the install prompt
        globalDeferredPrompt.prompt();

        // Wait for the user to respond to the prompt
        const { outcome } = await globalDeferredPrompt.userChoice;
        console.log(`User response to the install prompt: ${outcome}`);

        // We've used the prompt, and can't use it again, throw it away
        globalDeferredPrompt = null;
        globalIsInstallable = false;
        listeners.forEach(l => l(false));
    };

    if (isInstalled || !isVisible) return null;

    return (
        <motion.button
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            onClick={handleInstallClick}
            className={`flex items-center gap-3 px-3 py-3 rounded-2xl transition-all bg-primary/20 text-primary hover:bg-primary hover:text-white group ${className}`}
        >
            <Download size={20} className="group-hover:animate-bounce" />
            {showLabel && <span className="font-medium">Install App</span>}
        </motion.button>
    );
};

export default PWAInstallButton;
